/**
 * Cell colors for the Daily Photo List table, cards and print view.
 *
 * The server works each car's colors out from its dealer group's highlight
 * rules (backend/services/photo_list_highlighting.py) and sends them with the
 * list as `cell_colors` (column → color name) and `bold_cells`. This page
 * holds no rules — it paints what it is sent, so the Excel, this page and the
 * iPhone app can never disagree (plan: photo-list-rules-and-progress.md, Part B).
 */
import type { CSSProperties } from "react"

import type { PhotoListCellColor, PhotoListVehicle } from "../api/photoList"
import { THEME } from "../config/theme"

// --- Fill shades (the backend's HIGHLIGHT_FILL_COLORS) ---
// Exported so tests assert against the canonical values instead of
// re-declaring hex literals (which the anti-patterns CI check rejects).

export const FILL = {
  green: "#C6EFCE",
  yellow: "#FFF2CC",
  darkYellow: "#FAC97F",
  orange: "#FCE4D6",
  blue: "#D9E1F2",
  red: "#F4CCCC",
  lightGrey: "#CFCFCF",
  darkGrey: "#9C9C9C",
} as const

/** The server's color names → this page's shades. */
export const FILL_BY_COLOR: Record<PhotoListCellColor, string> = {
  green: FILL.green,
  yellow: FILL.yellow,
  "dark yellow": FILL.darkYellow,
  orange: FILL.orange,
  blue: FILL.blue,
  red: FILL.red,
  "light grey": FILL.lightGrey,
  "dark grey": FILL.darkGrey,
}

/** One cell's style: its color from the server, and bold black text where
 * the server says so (Toyota's STOP). Columns the server didn't color get
 * nothing. A list from an older server has no colors — every cell plain. */
export function getCellStyle(vehicle: PhotoListVehicle, columnKey: string): CSSProperties {
  const style: CSSProperties = {}
  const color = vehicle.cell_colors?.[columnKey]
  if (color && FILL_BY_COLOR[color]) style.backgroundColor = FILL_BY_COLOR[color]
  if (vehicle.bold_cells?.includes(columnKey)) {
    style.fontWeight = "700"
    style.color = THEME.colors.black
  }
  return style
}
